import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Building2, Save, UploadCloud } from "lucide-react";

import { PageHeader } from "@/components/common/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { companyService } from "@/services/company.service";
import { uploadsService } from "@/services/uploads.service";
import { getErrorMessage } from "@/services/api";
import { mediaUrl } from "@/lib/utils";

function Field({ label, children, hint }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs font-medium">{label}</Label>
            {children}
            {hint && <div className="text-[11px] text-muted-foreground">{hint}</div>}
        </div>
    );
}

export default function CompanySettingsPage() {
    const qc = useQueryClient();
    const company = useQuery({ queryKey: ["company"], queryFn: () => companyService.get() });

    const {
        register, handleSubmit, reset, watch, setValue,
        formState: { isDirty, isSubmitting },
    } = useForm({
        defaultValues: {
            legal_name: "", trade_name: "", tax_id: "",
            email: "", phone: "", website: "",
            address: "", city: "", state: "", zip_code: "",
            responsible_engineer: "", engineering_license: "",
            logo_url: "",
        },
    });

    useEffect(() => {
        if (company.data) {
            reset({
                legal_name: company.data.legal_name || "",
                trade_name: company.data.trade_name || "",
                tax_id: company.data.tax_id || "",
                email: company.data.email || "",
                phone: company.data.phone || "",
                website: company.data.website || "",
                address: company.data.address || "",
                city: company.data.city || "",
                state: company.data.state || "",
                zip_code: company.data.zip_code || "",
                responsible_engineer: company.data.responsible_engineer || "",
                engineering_license: company.data.engineering_license || "",
                logo_url: company.data.logo_url || "",
            });
        }
    }, [company.data, reset]);

    const save = useMutation({
        mutationFn: (data) => companyService.update(data),
        onSuccess: (updated) => {
            toast.success("Dados da empresa atualizados.");
            qc.invalidateQueries({ queryKey: ["company"] });
            reset(updated);
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao salvar dados da empresa.")),
    });

    async function handleLogoUpload(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        e.target.value = "";
        try {
            const uploaded = await uploadsService.upload(file);
            setValue("logo_url", uploaded.url, { shouldDirty: true });
            toast.success("Logo enviada. Clique em Salvar para confirmar.");
        } catch (err) {
            toast.error(getErrorMessage(err, "Falha ao enviar logo."));
        }
    }

    if (company.isLoading) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-12 w-80" />
                <Skeleton className="h-96" />
            </div>
        );
    }

    const currentLogo = watch("logo_url");

    return (
        <form
            onSubmit={handleSubmit(save.mutate)}
            className="space-y-6"
            data-testid="company-settings-page"
        >
            <PageHeader
                title="Configurações da Empresa"
                description="Dados institucionais usados no cabeçalho das propostas comerciais e PDFs oficiais."
                breadcrumb={[
                    { label: "Início", to: "/dashboard" },
                    { label: "Configurações" },
                    { label: "Empresa" },
                ]}
                actions={(
                    <Button
                        type="submit"
                        disabled={!isDirty || isSubmitting || save.isPending}
                        className="bg-secondary text-secondary-foreground hover:opacity-90"
                        data-testid="company-save-button"
                    >
                        <Save className="mr-2 h-4 w-4" /> Salvar
                    </Button>
                )}
            />

            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        Identidade
                    </CardTitle>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-[220px_1fr]">
                    {/* Logo */}
                    <div className="space-y-2">
                        <Label className="text-xs font-medium">Logo da empresa</Label>
                        <div className="relative flex aspect-square items-center justify-center overflow-hidden rounded-md border bg-muted/30">
                            {currentLogo ? (
                                // eslint-disable-next-line jsx-a11y/img-redundant-alt
                                <img
                                    src={mediaUrl(currentLogo)}
                                    alt="Logo"
                                    className="h-full w-full object-contain"
                                    data-testid="company-logo-preview"
                                />
                            ) : (
                                <div className="text-center text-[11px] text-muted-foreground">
                                    Sem logo cadastrada
                                </div>
                            )}
                        </div>
                        <label className="flex h-9 cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed bg-background px-3 text-xs font-medium hover:border-secondary/60">
                            <UploadCloud className="h-3.5 w-3.5" />
                            Trocar logo
                            <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} data-testid="company-logo-input" />
                        </label>
                        <div className="text-[10px] text-muted-foreground">
                            Usada no cabeçalho das propostas PDF.
                        </div>
                    </div>
                    {/* Basic identity */}
                    <div className="grid gap-3 md:grid-cols-2">
                        <Field label="Razão social">
                            <Input {...register("legal_name")} data-testid="company-legal-name" />
                        </Field>
                        <Field label="Nome fantasia">
                            <Input {...register("trade_name")} data-testid="company-trade-name" />
                        </Field>
                        <Field label="CNPJ">
                            <Input {...register("tax_id")} placeholder="00.000.000/0001-00" data-testid="company-tax-id" />
                        </Field>
                        <Field label="E-mail">
                            <Input type="email" {...register("email")} data-testid="company-email" />
                        </Field>
                        <Field label="Telefone">
                            <Input {...register("phone")} data-testid="company-phone" />
                        </Field>
                        <Field label="Website">
                            <Input {...register("website")} placeholder="https://" data-testid="company-website" />
                        </Field>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold">Endereço</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-3 md:grid-cols-[2fr_1fr_100px_120px]">
                    <Field label="Endereço">
                        <Input {...register("address")} data-testid="company-address" />
                    </Field>
                    <Field label="Cidade">
                        <Input {...register("city")} data-testid="company-city" />
                    </Field>
                    <Field label="UF">
                        <Input {...register("state")} maxLength={2} data-testid="company-state" />
                    </Field>
                    <Field label="CEP">
                        <Input {...register("zip_code")} data-testid="company-zip" />
                    </Field>
                </CardContent>
            </Card>

            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold">Responsabilidade Técnica</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-3 md:grid-cols-2">
                    <Field label="Responsável técnico" hint="Nome do engenheiro responsável (aparece no PDF)">
                        <Input {...register("responsible_engineer")} data-testid="company-responsible" />
                    </Field>
                    <Field label="CREA / Registro" hint="Número de registro no conselho profissional">
                        <Input {...register("engineering_license")} data-testid="company-crea" />
                    </Field>
                </CardContent>
            </Card>
        </form>
    );
}
